/**
 * SEO Configuration for Public Routes
 * Meta tags, OG tags, Twitter tags, and canonical URLs for each page
 * 
 * Usage: <SEO page="home" /> or <SEO page="/" />
 */

const BASE_URL = process.env.REACT_APP_FE_URL;

// Page name to route mapping
export const PAGE_ROUTE_MAP = {
    'home': '/',
    'web-analyser': '/web-analyser',
    'web-analyzer': '/web-analyser', // Alias
    'web-analyser-result': '/web-analyser/result', // Base route pattern
    'studio': '/studio',
    'password-generator': '/password-generator',
    'password-generator-embed': '/password-generator/embed',
    'json-formatter': '/json-formatter',
    'json-viewer': '/json-viewer',
    'base64-encoder-decoder': '/base64-encoder-decoder',
    'text-case-converter': '/text-case-converter',
    'hash-generator': '/hash-generator',
    'file-hash-calculator': '/file-hash-calculator',
    'color-picker': '/color-picker',
    'material-design-colors': '/material-design-colors',
    'font-pairing-tool': '/font-pairing-tool',
    'uuid-generator': '/uuid-generator',
    'ulid-generator': '/ulid-generator',
    'guid-generator': '/guid-generator',
    'password-strength-checker': '/password-strength-checker',
    'code-minifier': '/code-minifier',
    'url-encoder-decoder': '/url-encoder-decoder',
    'url-parser': '/url-parser',
    'utm-builder': '/utm-builder',
    'utm-campaign-builder': '/utm-campaign-builder',
    'utm-parser': '/utm-parser',
    'url-builder': '/url-builder',
    'code-formatter': '/code-formatter',
    'regex-tester': '/regex-tester',    'color-contrast-checker': '/color-contrast-checker',
    'meta-tags-generator': '/meta-tags-generator',
    'csp-generator': '/csp-generator',
    'twitter-card-generator': '/twitter-card-generator',
    'robots-txt-generator': '/robots-txt-generator',
    'open-graph-tags-generator': '/open-graph-tags-generator',
    'meta-description-generator': '/meta-description-generator',
    'title-tag-optimizer': '/title-tag-optimizer',
    'date-calculator': '/date-calculator',
    'timer-tool': '/timer-tool',
    'csv-to-json-converter': '/csv-to-json-converter',
    'csv-viewer': '/csv-viewer',
    'json-to-xml-converter': '/json-to-xml-converter',
    'xml-to-json-converter': '/xml-to-json-converter',
    'color-converter': '/color-converter',
    'color-shades-generator': '/color-shades-generator',
    'color-palette-generator': '/color-palette-generator',
    'gradient-generator': '/gradient-generator',
    'css-animation-generator': '/css-animation-generator',
    'box-shadow-generator': '/box-shadow-generator',
    'border-radius-generator': '/border-radius-generator',
    'timezone-converter': '/timezone-converter',
    'number-base-converter': '/number-base-converter',
    'unit-converter': '/unit-converter',
    'schema-markup-generator': '/schema-markup-generator',
    'lorem-ipsum-generator': '/lorem-ipsum-generator',    'image-studio': '/image-studio',
    'json-minifier': '/json-minifier',
    'random-number-generator': '/random-number-generator',
};

// SEO Configuration by page name
export const SEO_CONFIG = {
    // Homepage
    'home': {
        title: 'DGTIA Tools - Free Web Analysis & SEO Tools',
        description: 'Free web analysis tools including Web Analyser, Password Generator, JSON Formatter, and more. Analyze your website performance, generate secure passwords, format code, and improve SEO. Free forever, no signup, no ads, unlimited usage.',
        keywords: 'web analyzer, password generator, JSON formatter, SEO tools, website analysis, free tools, website performance, SEO checker',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Web Analyser Tool
    'web-analyser': {
        title: 'Web Analyser - Free Website Analysis Tool | DGTIA Tools',
        description: 'Analyze your website performance, check page speed, mobile responsiveness, and get detailed SEO insights. Free website analysis tool. Full results require free OTP verification. No signup, no payment required.',
        keywords: 'web analyzer, website analysis, page speed, SEO analysis, website checker, performance analyzer, free SEO tool',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/web-analyser`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Web Analyser Result Page (fallback when no dynamic data)
    'web-analyser-result': {
        title: 'Web Analysis Report | DGTIA Tools',
        description: 'View comprehensive website analysis report including performance, SEO, security, and accessibility scores. Get detailed insights and recommendations.',
        keywords: 'web analysis report, website analysis results, SEO report, performance report, security analysis, accessibility report',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/web-analyser/result`,
        ogType: 'article',
        ogLocale: 'en_AE',
    },

    // Studio Coming Soon Page
    'studio': {
        title: 'Studio - Coming Soon | DGTIA Tools',
        description: 'Advanced website analysis tools coming soon. Scheduled tests, advanced analytics, security monitoring, and professional reporting features.',
        keywords: 'advanced web analysis, scheduled tests, website analytics, professional tools, coming soon, studio',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/studio`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Password Generator Tool
    'password-generator': {
        title: 'Password Generator - Free Secure Password Tool | DGTIA Tools',
        description: 'Generate secure, random passwords with customizable options. Free password generator with uppercase, numbers, symbols. 100% client-side, no signup, no data stored, free forever.',
        keywords: 'password generator, secure password, random password, password tool, free password generator, strong password, password creator',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/password-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Password Generator Embed Page
    'password-generator-embed': {
        title: 'Embed Password Generator Widget - Add to Site | DGTIA Tools',
        description: 'Embed our free password generator widget on your website. Customize size, colors, theme, and styling. Get embed code instantly. 100% client-side.',
        keywords: 'password generator widget, embed password generator, password tool widget, embed code, website widget, password generator iframe',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/password-generator/embed`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // JSON Formatter Tool
    'json-formatter': {
        title: 'JSON Formatter - Format & Validate JSON | DGTIA Tools',
        description: 'Format and validate JSON with syntax highlighting. Free JSON formatter with beautify, minify, validation. 100% client-side, no data stored.',
        keywords: 'json formatter, json validator, json beautifier, json minify, format json, validate json, json tool, json parser, free json formatter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/json-formatter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Base64 Encoder/Decoder Tool
    'base64-encoder-decoder': {
        title: 'Base64 Encoder/Decoder - Encode & Decode | DGTIA Tools',
        description: 'Encode text to Base64 or decode Base64 to text instantly. Free Base64 encoder/decoder tool. 100% client-side, no data stored, completely private.',
        keywords: 'base64 encoder, base64 decoder, base64 encode, base64 decode, base64 converter, base64 tool, encode base64, decode base64, free base64 encoder',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/base64-encoder-decoder`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Text Case Converter Tool
    'text-case-converter': {
        title: 'Text Case Converter - Convert Text Case | DGTIA Tools',
        description: 'Convert text between uppercase, lowercase, title case, sentence case, and more. Free text case converter. 100% client-side, instant conversion, no data stored.',
        keywords: 'text case converter, uppercase converter, lowercase converter, title case converter, sentence case, text formatter, case converter, text tool, free case converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/text-case-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Unix Timestamp Converter Tool
    'unix-timestamp-converter': {
        title: 'Unix Timestamp Converter - Convert to Date | DGTIA Tools',
        description: 'Convert Unix timestamps to dates and vice versa instantly. Free Unix timestamp converter supporting seconds and milliseconds. 100% client-side, no data stored.',
        keywords: 'unix timestamp converter, timestamp to date, date to timestamp, epoch time converter, unix time converter, timestamp converter, epoch converter, free timestamp converter, unix date converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/unix-timestamp-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Timer Tool
    'timer-tool': {
        title: 'Timer Tool - Countdown Timer, Stopwatch & Interval Timer | DGTIA Tools',
        description: 'Free timer tool with countdown timer, stopwatch with lap functionality, and interval timer for workouts. Includes sound notifications. 100% client-side, no data stored.',
        keywords: 'timer tool, countdown timer, stopwatch, interval timer, workout timer, time tracker, free timer, online timer, timer with sound, lap timer, exercise timer',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/timer-tool`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Gas Oil Mass Volume Converter Tool
    'gas-oil-mass-volume-converter': {
        title: 'Gas Oil Mass Volume Converter - Tonnes to Cubic Meters | DGTIA Tools',
        description: 'Convert metric tonnes of gas oil to cubic meters with ASTM D1250 correction factors instantly. Free gas oil mass volume converter with VCF, WCF, SCF calculations. 100% client-side.',
        keywords: 'gas oil mass volume converter, tonnes to cubic meters, gas oil storage calculator, fuel volume converter, ASTM D1250 calculator, VCF WCF calculator, fuel storage planning, free gas oil converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/gas-oil-mass-volume-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },
    // Fuel Oil Mass Volume Converter Tool
    'fuel-oil-mass-volume-converter': {
        title: 'Fuel Oil Mass Volume Converter - Tonnes to Cubic Meters | DGTIA Tools',
        description: 'Convert metric tonnes of fuel oil to cubic meters with ASTM D1250 correction factors instantly. Free fuel oil mass volume converter with VCF, WCF, SCF calculations. 100% client-side.',
        keywords: 'fuel oil mass volume converter, tonnes to cubic meters, fuel oil storage calculator, fuel volume converter, ASTM D1250 calculator, VCF WCF calculator, fuel storage planning, free fuel oil converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/fuel-oil-mass-volume-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Hash Generator Tool
    'hash-generator': {
        title: 'Hash Generator - MD5, SHA1, SHA256, SHA512 | DGTIA Tools',
        description: 'Generate MD5, SHA1, SHA256, SHA512 hashes from text instantly. Free hash generator with secure cryptographic algorithms. 100% client-side, no data stored.',
        keywords: 'hash generator, md5 generator, sha1 generator, sha256 generator, sha512 generator, hash calculator, cryptographic hash, free hash generator, text to hash, password hash generator',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/hash-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Color Picker Tool
    'color-picker': {
        title: 'Color Picker - HEX, RGB, HSL, HSV Color Tool | DGTIA Tools',
        description: 'Free color picker tool to pick colors and convert between HEX, RGB, HSL, HSV formats. Visual preview, instant conversion. 100% client-side, no data stored.',
        keywords: 'color picker, hex color picker, rgb color picker, hsl color picker, color converter, web color picker, free color picker, color palette generator, html color picker, color code picker',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/color-picker`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // UUID Generator Tool
    'uuid-generator': {
        title: 'UUID Generator - Generate UUIDs v1, v4 | DGTIA Tools',
        description: 'Generate UUIDs (v1, v4) for your applications instantly. Free UUID generator with bulk generation support. 100% client-side, instant generation, no data stored.',
        keywords: 'uuid generator, uuid v1, uuid v4, generate uuid, unique identifier, guid generator, uuid tool, free uuid generator, random uuid, uuid online generator',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/uuid-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },
    // ULID Tool
    'ulid-generator': {
        title: 'ULIDGenerator - DGTIA Tools',
        description: 'ULIDGenerator tool. Free, fast, and privacy-focused.',
        keywords: 'ulid-generator, ULID, tool, free, online',
        ogImage: '/images/og-image.jpg',
        canonical: `\${BASE_URL}/ulid-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Password Strength Checker Tool
    'password-strength-checker': {
        title: 'Password Strength Checker - Check Password Security | DGTIA Tools',
        description: 'Check password strength with visual indicator. Free password strength checker with real-time analysis and security requirements. 100% client-side, no data stored.',
        keywords: 'password strength checker, password security, password validator, strong password, password strength meter, password analyzer, free password checker, password strength test',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/password-strength-checker`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Code Minifier Tool
    'code-minifier': {
        title: 'Code Minifier - Minify JS, CSS, HTML | DGTIA Tools',
        description: 'Minify JavaScript, CSS, HTML to reduce file size instantly. Free code minifier with instant processing and size statistics. 100% client-side, no data stored.',
        keywords: 'code minifier, javascript minifier, css minifier, html minifier, minify code, code compressor, file size reducer, free code minifier, js minifier',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/code-minifier`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Text Encoder/Decoder Tool (URL + HTML)
    'url-encoder-decoder': {
        title: 'Text Encoder/Decoder - URL & HTML Encoding | DGTIA Tools',
        description: 'Encode or decode URL strings and HTML entities instantly. Free text encoder/decoder supporting URL encoding and HTML entity encoding. 100% client-side, no data stored.',
        keywords: 'text encoder, text decoder, url encoder, url decoder, html encoder, html decoder, encode url, decode url, html entities, url encoding, html encoding, percent encoding, uri encoding, free text encoder',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/url-encoder-decoder`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // UTM Builder Tool
    'utm-builder': {
        title: 'UTM Builder - Create UTM Tracking URLs | DGTIA Tools',
        description: 'Build URLs with UTM parameters for marketing campaigns instantly. Free UTM builder to create tracking URLs with source, medium, campaign parameters. 100% client-side.',
        keywords: 'utm builder, utm generator, utm url builder, utm parameters, utm tracking, marketing url builder, campaign url builder, utm source, utm medium, free utm builder',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/utm-builder`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Code Formatter Tool
    'code-formatter': {
        title: 'Code Formatter - Format JS, CSS, HTML, JSON | DGTIA Tools',
        description: 'Format and beautify JavaScript, CSS, HTML, JSON code instantly. Free code formatter with proper indentation and spacing. 100% client-side, no data stored.',
        keywords: 'code formatter, code beautifier, javascript formatter, css formatter, html formatter, json formatter, code prettifier, format code, beautify code, free code formatter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/code-formatter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Regex Tester Tool
    'regex-tester': {
        title: 'Regex Tester - Test Regular Expressions | DGTIA Tools',
        description: 'Test regular expressions with live matching and highlighting. Free regex tester to debug and validate regex patterns. 100% client-side, no data stored.',
        keywords: 'regex tester, regex tester online, regular expression tester, regex debugger, regex validator, test regex, regex matcher, regex tool, free regex tester',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/regex-tester`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Color Contrast Checker Tool
    'color-contrast-checker': {
        title: 'Color Contrast Checker - WCAG Accessibility | DGTIA Tools',
        description: 'Check color contrast ratios for WCAG accessibility compliance instantly. Free color contrast checker to ensure designs meet AA and AAA standards. 100% client-side.',
        keywords: 'color contrast checker, wcag contrast checker, accessibility checker, color contrast ratio, wcag compliance, contrast ratio calculator, accessibility tool, free contrast checker, wcag checker',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/color-contrast-checker`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Meta Tags Generator Tool
    'meta-tags-generator': {
        title: 'Meta Tags Generator - Generate SEO Meta Tags | DGTIA Tools',
        description: 'Generate optimized meta tags for SEO and social media sharing. Free meta tags generator with Open Graph and Twitter Card support. 100% client-side, no data stored.',
        keywords: 'meta tags generator, meta tag generator, seo meta tags, open graph generator, twitter card generator, meta description generator, html meta tags, free meta tag generator, seo meta tag generator',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/meta-tags-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Open Graph Tags Generator Tool
    'open-graph-tags-generator': {
        title: 'Open Graph Tags Generator - Generate OG Tags | DGTIA Tools',
        description: 'Generate Open Graph meta tags for social media sharing. Free OG tags generator for Facebook, LinkedIn, and other platforms. 100% client-side, no data stored.',
        keywords: 'open graph generator, og tags generator, facebook meta tags, social media tags, og:title, og:image, og:description, free og tags generator, og meta tags',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/open-graph-tags-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Meta Description Generator Tool
    'meta-description-generator': {
        title: 'Meta Description Generator - SEO Meta Descriptions | DGTIA Tools',
        description: 'Generate optimized meta descriptions with character count validation instantly. Free meta description generator for better SEO and click-through rates. 100% client-side.',
        keywords: 'meta description generator, meta description tool, seo meta description, meta description writer, free meta description generator, seo description generator',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/meta-description-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Title Tag Optimizer Tool
    'title-tag-optimizer': {
        title: 'Title Tag Optimizer - Optimize SEO Title Tags | DGTIA Tools',
        description: 'Optimize title tags with length validation for better SEO. Free title tag optimizer with character count and search preview. 100% client-side, no data stored.',
        keywords: 'title tag optimizer, title tag generator, seo title tag, optimize title tag, title tag tool, free title tag optimizer, seo title optimizer',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/title-tag-optimizer`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Date Calculator Tool
    'date-calculator': {
        title: 'Date Calculator - Calculate Date Difference | DGTIA Tools',
        description: 'Calculate the difference between two dates in days, weeks, months, and years instantly. Free date calculator with detailed breakdown. 100% client-side.',
        keywords: 'date calculator, date difference calculator, days between dates, date calculator online, free date calculator',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/date-calculator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Date Formatter Tool
    'date-formatter': {
        title: 'Date Formatter - Format Dates in Various Formats | DGTIA Tools',
        description: 'Format dates in various formats including ISO, US, European, Unix timestamp, RFC 2822 instantly. Free date formatter with multiple format previews. 100% client-side, no data stored.',
        keywords: 'date formatter, date format converter, date format tool, format date online, free date formatter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/date-formatter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // JSON to CSV Converter Tool
    'json-to-csv-converter': {
        title: 'JSON to CSV Converter - Convert JSON to CSV Format | DGTIA Tools',
        description: 'Convert JSON arrays to CSV format instantly. Free JSON to CSV converter for data analysis, spreadsheet import, and data migration. 100% client-side, no data stored.',
        keywords: 'json to csv, json csv converter, convert json to csv, json to csv online, free json csv converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/json-to-csv-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // CSV to JSON Converter Tool
    'csv-to-json-converter': {
        title: 'CSV to JSON Converter - Convert CSV to JSON Format | DGTIA Tools',
        description: 'Convert CSV data to JSON format instantly. Free CSV to JSON converter for API integration, data processing, and web development. 100% client-side, no data stored.',
        keywords: 'csv to json, csv json converter, convert csv to json, csv to json online, free csv json converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/csv-to-json-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // JSON to XML Converter Tool
    'json-to-xml-converter': {
        title: 'JSON to XML Converter - Convert JSON to XML Format | DGTIA Tools',
        description: 'Convert JSON data to XML format instantly. Free JSON to XML converter for API integration, data exchange, and legacy system compatibility. 100% client-side, no data stored.',
        keywords: 'json to xml, json xml converter, convert json to xml, json to xml online, free json xml converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/json-to-xml-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // XML to JSON Converter Tool
    'xml-to-json-converter': {
        title: 'XML to JSON Converter - Convert XML to JSON Format | DGTIA Tools',
        description: 'Convert XML data to JSON format instantly. Free XML to JSON converter for modern web applications and APIs. 100% client-side, no data stored.',
        keywords: 'xml to json, xml json converter, convert xml to json, xml to json online, free xml json converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/xml-to-json-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Color Converter Tool
    'color-converter': {
        title: 'Color Converter - HEX, RGB, HSL, HSV, CMYK | DGTIA Tools',
        description: 'Convert colors between HEX, RGB, HSL, HSV, CMYK formats instantly. Free color converter with visual preview for web and print design. 100% client-side, no data stored.',
        keywords: 'color converter, hex to rgb, rgb to hex, hsl converter, hsv converter, cmyk converter, color format converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/color-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Color Shades Generator Tool
    'color-shades-generator': {
        title: 'Color Shades Generator - Generate Shades & Tints | DGTIA Tools',
        description: 'Generate color shades and tints from any base color instantly. Free color palette generator with darker shades and lighter tints. Perfect for consistent color schemes. 100% client-side, no data stored.',
        keywords: 'color shades generator, color tints generator, color palette generator, shade generator, tint generator, color scheme generator',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/color-shades-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Gradient Generator Tool
    'gradient-generator': {
        title: 'Gradient Generator - Generate CSS Gradients | DGTIA Tools',
        description: 'Generate beautiful CSS linear gradients with custom colors and directions instantly. Free gradient generator with live preview and ready-to-use CSS. 100% client-side, no data stored.',
        keywords: 'gradient generator, css gradient generator, linear gradient generator, gradient maker, css gradient maker, free gradient generator',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/gradient-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Timezone Converter Tool
    'timezone-converter': {
        title: 'Timezone Converter - Convert Time Between Timezones | DGTIA Tools',
        description: 'Convert date and time between different timezones instantly. Free timezone converter for UTC, EST, PST, GMT, IST, and more. Perfect for global coordination. 100% client-side.',
        keywords: 'timezone converter, time converter, timezone converter online, convert timezone, time zone converter, free timezone converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/timezone-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Number Base Converter Tool
    'number-base-converter': {
        title: 'Number Base Converter - Binary, Decimal, Hex, Octal | DGTIA Tools',
        description: 'Convert numbers between binary (2), octal (8), decimal (10), hexadecimal (16) bases instantly. Free number base converter for programming. 100% client-side, no data stored.',
        keywords: 'number base converter, binary converter, hex converter, octal converter, base converter, number system converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/number-base-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Unit Converter Tool
    'unit-converter': {
        title: 'Unit Converter - Convert Length, Weight, Temperature | DGTIA Tools',
        description: 'Convert between different units of length, weight, and temperature instantly. Free unit converter for meters, kilograms, Celsius, Fahrenheit, and more. 100% client-side.',
        keywords: 'unit converter, length converter, weight converter, temperature converter, unit conversion, measurement converter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/unit-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Schema Markup Generator Tool
    'schema-markup-generator': {
        title: 'Schema Markup Generator - JSON-LD Structured Data | DGTIA Tools',
        description: 'Generate structured data markup (JSON-LD) for SEO instantly. Free schema markup generator for Organization, Article, Product, FAQ, Breadcrumb. 100% client-side, no data stored.',
        keywords: 'schema markup generator, json-ld generator, structured data generator, schema.org generator, rich snippets generator, seo schema, schema markup tool, free schema generator',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/schema-markup-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Text to Slug Converter Tool
    // Text Counter Tool
    // Lorem Ipsum Generator Tool
    'lorem-ipsum-generator': {
        title: 'Lorem Ipsum Generator - Placeholder Text | DGTIA Tools',
        description: 'Generate Lorem Ipsum placeholder text in words, sentences, or paragraphs. Free Lorem Ipsum generator with customizable amount. 100% client-side, no data stored.',
        keywords: 'lorem ipsum generator, placeholder text generator, dummy text generator, lorem ipsum, placeholder text, dummy text, free lorem ipsum generator, lorem ipsum text',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/lorem-ipsum-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // HTML Encoder/Decoder Tool

    // JSON Minifier Tool
    'json-minifier': {
        title: 'JSON Minifier - Minify JSON | DGTIA Tools',
        description: 'Minify JSON to reduce file size and improve performance. Free JSON minifier with validation and size statistics. 100% client-side, no data stored.',
        keywords: 'json minifier, minify json, json compressor, json optimizer, reduce json size, json minify tool, free json minifier, json compressor tool',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/json-minifier`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Random Number Generator Tool
    'random-number-generator': {
        title: 'Random Number Generator - Generate Random Numbers | DGTIA Tools',
        description: 'Generate random numbers within a specified range instantly. Free random number generator with custom min/max values. 100% client-side, no data stored.',
        keywords: 'random number generator, random number, random generator, random number picker, random integer generator, free random number generator, random number tool',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/random-number-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Text Reverser Tool
    // Percentage Calculator Tool
    // Age Calculator Tool
    // Week Number Calculator Tool
    // Prime Number Checker Tool
    // Text to Binary Converter Tool
    // Text to Hex Converter Tool
    // Unicode Converter Tool
    // ASCII Converter Tool
    // Morse Code Converter Tool
    // ROT13 Encoder/Decoder Tool
    // Factorial Calculator Tool
    // GCD/LCM Calculator Tool
    // HTTP Status Code Lookup Tool
    // MIME Type Lookup Tool
    // JWT Decoder Tool
    'jwt-decoder': {
        title: 'JWT Decoder - Decode JSON Web Tokens | DGTIA Tools',
        description: 'Decode and inspect JWT tokens instantly. Free JWT decoder to view header, payload, and signature. 100% client-side, no data stored.',
        keywords: 'jwt decoder, jwt token decoder, decode jwt, json web token decoder, jwt parser, jwt inspector, free jwt decoder, jwt token parser',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/jwt-decoder`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // URL Query String Parser Tool
    // File Size Converter Tool
    // Random String Generator Tool
    'random-string-generator': {
        title: 'Random String Generator - Secure Strings | DGTIA Tools',
        description: 'Generate cryptographically secure random strings with customizable length and character types. Free random string generator. 100% client-side, no data stored.',
        keywords: 'random string generator, random password generator, secure random string, random token generator, random string, free random string generator, random string tool',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/random-string-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // YAML to JSON Converter Tool
    'yaml-to-json-converter': {
        title: 'YAML to JSON Converter - Convert Online | DGTIA Tools',
        description: 'Convert YAML to JSON format instantly. Free YAML to JSON converter with real-time conversion. 100% client-side, no data stored.',
        keywords: 'yaml to json converter, yaml to json, convert yaml to json, yaml converter, json converter, free yaml to json converter, yaml json',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/yaml-to-json-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // JSON to YAML Converter Tool
    'json-to-yaml-converter': {
        title: 'JSON to YAML Converter - Convert Online | DGTIA Tools',
        description: 'Convert JSON to YAML format instantly. Free JSON to YAML converter with proper indentation. 100% client-side, no data stored.',
        keywords: 'json to yaml converter, json to yaml, convert json to yaml, json converter, yaml converter, free json to yaml converter, json yaml',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/json-to-yaml-converter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // IBAN Validator Tool
    // ISBN Validator Tool
    // Tools Catalog Page
    'tools-catalog': {
        title: 'Tools Catalog - Free Online Tools | DGTIA Tools',
        description: 'Browse all free tools available on DGTIA Tools. Quick access to web analysis, password generator, JSON formatter, and more developer tools. 100% client-side.',
        keywords: 'free tools, online tools, web tools, developer tools, tools catalog, tools list, bookmark tools, free online tools',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/catalog`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },


    // Image Studio Tool
    'image-studio': {
        title: 'Image Studio - Comprehensive Image Processing Tool | DGTIA Tools',
        description: 'All-in-one image processing: rotate, flip, compress, convert formats, apply filters, extract colors, view metadata, and more. 100% free, client-side processing.',
        keywords: 'image editor, image processor, image converter, image compressor, image optimizer, image tools, photo editor, image filters, image metadata, color extractor, free image tools',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/image-studio`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // QR Code Generator Tool
    'qr-code-generator': {
        title: 'QR Code Generator - Generate QR Codes | DGTIA Tools',
        description: 'Generate QR codes from text or URLs instantly. Download or copy your QR code. Free QR code generator, 100% client-side, no data stored.',
        keywords: 'qr code generator, qr code maker, qr code creator, generate qr code, qr code online, free qr code generator, qr code tool',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/qr-code-generator`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Base64 to Image Tool
    'base64-to-image': {
        title: 'Base64 to Image - Convert Base64 Online | DGTIA Tools',
        description: 'Convert Base64 strings back to images instantly. Preview and download your images. Free base64 decoder, 100% client-side, no uploads.',
        keywords: 'base64 to image, base64 decoder, base64 image converter, convert base64 to image, base64 to png, base64 image decoder, free base64 decoder',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/base64-to-image`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Code Beautifier Tool

    // Syntax Highlighter Tool
    'syntax-highlighter': {
        title: 'Syntax Highlighter - Highlight Code Syntax | DGTIA Tools',
        description: 'Highlight syntax of your code with beautiful color schemes instantly. Supports 17+ languages including JavaScript, Python, Java, C++. 100% client-side.',
        keywords: 'syntax highlighter, code highlighter, syntax highlighting, code syntax, highlight code, code formatter, syntax colorizer, free syntax highlighter',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/syntax-highlighter`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Error 404 Page
    'error-404': {
        title: '404 - Page Not Found | DGTIA Tools',
        description: 'The page you are looking for doesn\'t exist or was moved. Return to DGTIA Tools homepage.',
        keywords: '404, page not found, error, DGTIA Tools',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/404`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Error 403 Page
    'error-403': {
        title: '403 - Access Forbidden | DGTIA Tools',
        description: 'You do not have access to this resource. Please contact support if you believe this is an error.',
        keywords: '403, access forbidden, unauthorized, DGTIA Tools',
        ogImage: '/images/og-image.jpg',
        canonical: `${BASE_URL}/403`,
        ogType: 'website',
        ogLocale: 'en_AE',
    },

    // Default fallback
    default: {
        title: 'DGTIA Tools - Free Web Analysis & SEO Tools',
        description: 'Free web analysis tools for website optimization and SEO.',
        keywords: 'web tools, SEO, website analysis',
        ogImage: '/images/og-image.jpg',
        canonical: BASE_URL,
        ogType: 'website',
        ogLocale: 'en_AE',
    }
};

// Legacy route-based config (for backward compatibility with SEOHead)
export const SEO_ROUTE_CONFIG = {
    '/': SEO_CONFIG['home'],
    '/web-analyser': SEO_CONFIG['web-analyser'],
    '/studio': SEO_CONFIG['studio'],
    '/password-generator': SEO_CONFIG['password-generator'],
    '/password-generator/embed': SEO_CONFIG['password-generator-embed'],
    '/json-formatter': SEO_CONFIG['json-formatter'],
    '/json-viewer': SEO_CONFIG['json-viewer'],
    '/base64-encoder-decoder': SEO_CONFIG['base64-encoder-decoder'],
    '/text-case-converter': SEO_CONFIG['text-case-converter'],
    '/unix-timestamp-converter': SEO_CONFIG['unix-timestamp-converter'],
    '/timer-tool': SEO_CONFIG['timer-tool'],
    '/gas-oil-mass-volume-converter': SEO_CONFIG['gas-oil-mass-volume-converter'],
    '/fuel-oil-mass-volume-converter': SEO_CONFIG['fuel-oil-mass-volume-converter'],
    '/hash-generator': SEO_CONFIG['hash-generator'],
    '/file-hash-calculator': SEO_CONFIG['file-hash-calculator'],
    '/color-picker': SEO_CONFIG['color-picker'],
    '/material-design-colors': SEO_CONFIG['material-design-colors'],
    '/font-pairing-tool': SEO_CONFIG['font-pairing-tool'],
    '/uuid-generator': SEO_CONFIG['uuid-generator'],
    '/ulid-generator': SEO_CONFIG['ulid-generator'],
    '/guid-generator': SEO_CONFIG['guid-generator'],
    '/password-strength-checker': SEO_CONFIG['password-strength-checker'],
    '/code-minifier': SEO_CONFIG['code-minifier'],
    '/url-encoder-decoder': SEO_CONFIG['url-encoder-decoder'],
    '/url-parser': SEO_CONFIG['url-parser'],
    '/utm-builder': SEO_CONFIG['utm-builder'],
    '/utm-campaign-builder': SEO_CONFIG['utm-campaign-builder'],
    '/utm-parser': SEO_CONFIG['utm-parser'],
    '/url-builder': SEO_CONFIG['url-builder'],
    '/code-formatter': SEO_CONFIG['code-formatter'],
    '/regex-tester': SEO_CONFIG['regex-tester'],    '/color-contrast-checker': SEO_CONFIG['color-contrast-checker'],
    '/meta-tags-generator': SEO_CONFIG['meta-tags-generator'],
    '/csp-generator': SEO_CONFIG['csp-generator'],
    '/twitter-card-generator': SEO_CONFIG['twitter-card-generator'],
    '/robots-txt-generator': SEO_CONFIG['robots-txt-generator'],
    '/open-graph-tags-generator': SEO_CONFIG['open-graph-tags-generator'],
    '/meta-description-generator': SEO_CONFIG['meta-description-generator'],
    '/title-tag-optimizer': SEO_CONFIG['title-tag-optimizer'],
    '/date-calculator': SEO_CONFIG['date-calculator'],
    '/date-formatter': SEO_CONFIG['date-formatter'],
    '/json-to-csv-converter': SEO_CONFIG['json-to-csv-converter'],
    '/csv-to-json-converter': SEO_CONFIG['csv-to-json-converter'],
    '/csv-viewer': SEO_CONFIG['csv-viewer'],
    '/json-to-xml-converter': SEO_CONFIG['json-to-xml-converter'],
    '/xml-to-json-converter': SEO_CONFIG['xml-to-json-converter'],
    '/color-converter': SEO_CONFIG['color-converter'],
    '/color-shades-generator': SEO_CONFIG['color-shades-generator'],
    '/color-palette-generator': SEO_CONFIG['color-palette-generator'],
    '/gradient-generator': SEO_CONFIG['gradient-generator'],
    '/css-animation-generator': SEO_CONFIG['css-animation-generator'],
    '/box-shadow-generator': SEO_CONFIG['box-shadow-generator'],
    '/border-radius-generator': SEO_CONFIG['border-radius-generator'],
    '/timezone-converter': SEO_CONFIG['timezone-converter'],
    '/number-base-converter': SEO_CONFIG['number-base-converter'],
    '/unit-converter': SEO_CONFIG['unit-converter'],
    '/schema-markup-generator': SEO_CONFIG['schema-markup-generator'],
    '/lorem-ipsum-generator': SEO_CONFIG['lorem-ipsum-generator'],
    '/json-minifier': SEO_CONFIG['json-minifier'],
    '/random-number-generator': SEO_CONFIG['random-number-generator'],
    '/jwt-decoder': SEO_CONFIG['jwt-decoder'],
    '/user-agent-parser': SEO_CONFIG['user-agent-parser'],
    '/random-string-generator': SEO_CONFIG['random-string-generator'],
    '/yaml-to-json-converter': SEO_CONFIG['yaml-to-json-converter'],
    '/json-to-yaml-converter': SEO_CONFIG['json-to-yaml-converter'],
    '/image-studio': SEO_CONFIG['image-studio'],
    '/qr-code-generator': SEO_CONFIG['qr-code-generator'],
    '/barcode-generator': SEO_CONFIG['barcode-generator'],
    '/base64-to-image': SEO_CONFIG['base64-to-image'],    '/syntax-highlighter': SEO_CONFIG['syntax-highlighter'],
    '/catalog': SEO_CONFIG['tools-catalog'],
    '/404': SEO_CONFIG['error-404'],
    '/403': SEO_CONFIG['error-403'],
    default: SEO_CONFIG.default
};

